// Attachment validation for the report modal — kind + size. Pure +
// dependency-free so it's unit-testable without a real File/upload.
//
// An incident is an operational record, so the evidence is whatever the reporter
// has to hand: a photo of the wrong room, the supplier's PDF voucher, a voice
// note from the driver, a signed complaint form. Images and video used to be the
// only accepted kinds; documents and audio are now first-class.
//
// Kept in sync with the `fileUploader` uploadthing endpoint
// (packages/uploadthing/src/server/file-router.ts): this validator is the
// friendly, per-kind gate, that route is the hard server limit. A kind allowed
// here but not there would fail mid-upload with an opaque error.

import type { AttachmentKind } from '@incidents/entities/incident';
import { resolveAttachmentKind } from '@incidents/entities/incident';

export const MAX_IMAGE_BYTES = 16 * 1024 * 1024;
export const MAX_DOCUMENT_BYTES = 32 * 1024 * 1024;
export const MAX_AUDIO_BYTES = 64 * 1024 * 1024;
export const MAX_VIDEO_BYTES = 256 * 1024 * 1024;

type FileLike = { name: string; type: string; size: number };

const LIMIT: Record<AttachmentKind, number> = {
  image: MAX_IMAGE_BYTES,
  document: MAX_DOCUMENT_BYTES,
  audio: MAX_AUDIO_BYTES,
  video: MAX_VIDEO_BYTES,
};

const LIMIT_LABEL: Record<AttachmentKind, string> = {
  image: 'image too large (max 16MB)',
  document: 'document too large (max 32MB)',
  audio: 'audio too large (max 64MB)',
  video: 'video too large (max 256MB)',
};

// Returns an error message when the file is not an acceptable attachment, or
// null when it is.
export const validateAttachmentFile = (file: FileLike): string | null => {
  const kind = resolveAttachmentKind(file);
  if (!kind) {
    return `${file.name}: only images, documents, audio or video files are allowed.`;
  }
  if (file.size > LIMIT[kind]) {
    return `${file.name}: ${LIMIT_LABEL[kind]}.`;
  }
  return null;
};
